// Types
import {
  Artist,
  AuthState,
  Collection,
  EntityState,
  Genre,
  Invite,
  Label,
  LicenseRevenue,
  LicenseType,
  Profile,
  Region,
  Song,
  State,
  Track,
  TrackType,
  UserSongSample,
} from 'types'
import { ProfileDefaultValues } from 'modules/profile/models'

// functional entities selectors for composing
export const selectAll = (state: State) => state
export const selectProps = <T>(state: State, props: T): T => props

export const songEntities = (state: State): EntityState<Song> => state.entities.songs
export const trackEntities = (state: State): EntityState<Track> => state.entities.tracks
export const artistEntities = (state: State): EntityState<Artist> => state.entities.artists
export const profileEntities = (state: State): EntityState<Profile> => state.entities.profiles
export const licenseTypesEntities = (state: State): EntityState<LicenseType> => state.entities.licenseTypes
export const licenseRevenuesEntities = (state: State): EntityState<LicenseRevenue> => state.entities.licenseRevenues
export const genreEntities = (state: State): EntityState<Genre> => state.entities.genres
export const invitesEntities = (state: State): EntityState<Invite> => state.entities.invites
export const trackTypeEntities = (state: State): EntityState<TrackType> => state.entities.trackTypes
export const regionEntities = (state: State): EntityState<Region> => state.entities.regions
export const labelEntities = (state: State): EntityState<Label> => state.entities.labels
export const collectionEntities = (state: State): EntityState<Collection> => state.entities.collections
export const authDomain = (state: State): AuthState => state.auth

export const ownProfileEntity = (state: State): Profile => {
  const {
    auth: { profileId },
  } = state
  if (profileId) {
    const profile = state.entities.profiles[String(profileId)]
    if (profile) {
      return profile
    }
  }
  return ProfileDefaultValues
}

export const ownCollectionsSelector = (state: State): Array<Collection> => {
  const { collections } = state.entities
  const profile = ownProfileEntity(state)
  const ownCollections = Object.keys(collections)
    .map(id => collections[id])
    .filter(x => x.owner === profile.user && x.id !== profile.favorites_collection_id)
  return ownCollections
}

export type LicenseSelectorInfo = {
  sharePercentage: number | null | undefined
  description: string
  descriptionShort: string
  sampleType: number
  songCategory: number | null | undefined
  maxSeconds: number
}

export const licenseInfoSelector = (
  state: State,
  userSongSample: UserSongSample | null | undefined,
): LicenseSelectorInfo | null | undefined => {
  if (!userSongSample || !userSongSample.song) {
    return null
  }
  const { licenseTypes, licenseRevenues } = state.entities
  const licenseRevenueList = Object.keys(licenseRevenues).map(id => licenseRevenues[id])
  const { song, license_sample_type } = userSongSample as { song: Song; license_sample_type: number }
  const licenseType = licenseTypes[String(license_sample_type)]
  const licenseRevenueInfo =
    licenseType &&
    song &&
    licenseRevenueList.find(
      lRevenue => lRevenue.category === song.licenseinfo.license_price_category && lRevenue.sample_type === licenseType.id,
    )
  return {
    sharePercentage: licenseRevenueInfo?.share_percentage,
    description: licenseType?.description,
    descriptionShort: licenseType?.description_short,
    sampleType: licenseType?.id,
    songCategory: licenseRevenueInfo?.category,
    maxSeconds: licenseType?.max_seconds,
  }
}
